import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Globe2,
  Info,
  LoaderCircle,
  Mail,
  MessageCircle,
  Send,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { clientApi } from "../api/client";
import type { ChatMessage } from "../types/api";
const section =
  "mx-auto flex max-w-[1440px] flex-col gap-10 px-5 py-16 sm:px-10 lg:px-20 lg:py-24";
export function Landing() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [chatEmail, setChatEmail] = useState("");
  const [chat, setChat] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sendingChat, setSendingChat] = useState(false);
  const [chatError, setChatError] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let active = true;
    const loadMessages = () => {
      const chatId = localStorage.getItem("green_card_chat_id");
      const visitorToken = localStorage.getItem("green_card_chat_token");
      if (!chatId || !visitorToken) return Promise.resolve();
      return clientApi.anonymousMessages(chatId, visitorToken)
      .then(({ data }) => {
        if (!active) return;
        setMessages(data);
        setSent(data.length > 0);
      })
      .catch(() => { if (active) setMessages([]); });
    };
    void loadMessages();
    const interval = window.setInterval(() => { void loadMessages(); }, 3000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);
  useEffect(() => {
    const messagesContainer = messagesEndRef.current;
    if (messagesContainer) {
      messagesContainer.scrollTo({ top: messagesContainer.scrollHeight, behavior: "smooth" });
    }
  }, [messages.length]);
  const submitEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (email)
      window.location.href = `/login?email=${encodeURIComponent(email)}`;
  };
  const submitChat = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = chat.trim();
    if (!body || sendingChat) return;
    setSendingChat(true);
    setChatError(false);
    try {
      const { data: message } = await clientApi.createChat(body, chatEmail);
      setMessages((current) => [...current, message]);
      setSent(true);
      setChat("");
      setEmailSaved(Boolean(chatEmail));
    } catch {
      setChatError(true);
    } finally {
      setSendingChat(false);
    }
  };
  const confirmChatEmail = async () => {
    const normalizedEmail = chatEmail.trim();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) return;
    const chatId = localStorage.getItem("green_card_chat_id");
    const visitorToken = localStorage.getItem("green_card_chat_token");
    if (!chatId || !visitorToken) return;
    try {
      await clientApi.identifyChat(chatId, visitorToken, normalizedEmail);
      const { data } = await clientApi.anonymousMessages(chatId, visitorToken);
      setMessages(data);
      setSent(data.length > 0);
      setEmailSaved(true);
    } catch {
      setEmailSaved(false);
    }
  };
  const helps = [
    [t("landing.helpItems.0.title"), t("landing.helpItems.0.text")],
    [t("landing.helpItems.1.title"), t("landing.helpItems.1.text")],
    [t("landing.helpItems.2.title"), t("landing.helpItems.2.text")],
  ];
  const steps = [
    t("landing.stepItems.0.title"),
    t("landing.stepItems.1.title"),
    t("landing.stepItems.2.title"),
    t("landing.stepItems.3.title"),
  ];
  const stepTexts = [
    t("landing.stepItems.0.text"),
    t("landing.stepItems.1.text"),
    t("landing.stepItems.2.text"),
    t("landing.stepItems.3.text"),
  ];
  const faq = [0, 1, 2, 3, 4, 5, 6].map((index) => ({
    question: t(`landing.faqItems.${index}.question`),
    answer: t(`landing.faqItems.${index}.answer`),
  }));
  return (
    <main>
      <section className="mx-auto flex max-w-[1440px] flex-col gap-10 px-5 pb-12 pt-8 sm:px-10 lg:flex-row lg:gap-16 lg:px-20 lg:pb-16 lg:pt-10">
        <div className="flex flex-1 flex-col gap-7 lg:max-w-[650px]">
          <div className="flex items-center gap-2 text-[11px] font-semibold text-sage">
            <span className="h-1.5 w-1.5 rounded-full bg-sage" />
            {t("hero.eyebrow")}
          </div>
          <h1 className="display whitespace-pre-line text-5xl leading-[1.05] text-ink sm:text-7xl">
            {t("hero.title")}
          </h1>
          <p className="text-xl font-medium text-ink">{t("hero.program")}</p>
          <p className="leading-7 text-muted">{t("hero.subtitle")}</p>
          <form onSubmit={submitEmail} className="flex flex-col gap-3">
            <label className="text-sm font-medium">{t("hero.email")}</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="flex h-14 flex-1 items-center gap-2 rounded-lg border border-line bg-white px-4">
                <Mail size={19} className="text-muted" />
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("hero.placeholder")}
                  className="min-w-0 flex-1 bg-transparent outline-none"
                />
              </div>
              <button className="flex h-14 items-center justify-center gap-3 rounded-lg bg-navy px-6 text-sm font-semibold text-white">
                {t("hero.cta")}
                <ArrowRight size={18} />
              </button>
            </div>
            <label className="flex gap-2 text-xs text-muted">
              <input type="checkbox" required />
              {t("hero.privacy")}
            </label>
            <p className="text-xs text-muted">{t("hero.disclaimer")}</p>
          </form>
        </div>
        <div className="relative flex min-h-[390px] flex-1 flex-col justify-end overflow-hidden rounded-3xl bg-mist p-5 lg:min-h-[620px]">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "linear-gradient(135deg, rgba(255,255,255,.0), rgba(255,255,255,.0)), url('/new-york_water.png')",
            }}
            aria-hidden="true"
          />
          <div className="relative rounded-2xl bg-white p-6 shadow-sm">
            <div className="mb-3 flex justify-between text-xs font-semibold text-sage">
              {t("hero.program")}
              <ArrowUpRight size={19} className="text-navy" />
            </div>
            <p className="display whitespace-pre-line text-3xl leading-tight text-ink">
              {t("landing.ctaTitle")}
            </p>
          </div>
        </div>
      </section>
      <div className="flex items-start gap-3 bg-mist px-5 py-6 sm:px-10 lg:px-20">
        <Info size={20} className="shrink-0 text-navy" />
        <p className="text-sm leading-6 text-ink">{t("landing.notice")}</p>
      </div>
      <section id="about" className={section}>
        <span className="text-[11px] font-semibold uppercase text-sage">
          01 / {t("landing.aboutLabel")}
        </span>
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-24">
          <div>
            <h2 className="display whitespace-pre-line text-4xl leading-tight text-ink sm:text-5xl">
              {t("landing.aboutTitle")}
            </h2>
            <p className="mt-6 leading-7 text-muted">
              {t("landing.aboutText")}
            </p>
            <a
              href="https://dvprogram.state.gov"
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 font-semibold text-navy"
            >
              dvprogram.state.gov
              <ArrowUpRight size={17} />
            </a>
          </div>
          <div className="rounded-2xl bg-mist p-7">
            <div className="flex items-center gap-3 text-xl font-semibold">
              <Globe2 className="text-navy" />
              {t("landing.official")}
            </div>
            <p className="mt-3 leading-7 text-muted">
              {t("landing.officialText")}
            </p>
            <div className="my-6 h-px bg-line" />
            <div className="flex items-center gap-3 text-xl font-semibold">
              <MessageCircle className="text-navy" />
              {t("landing.service")}
            </div>
            <p className="mt-3 leading-7 text-muted">
              {t("landing.serviceText")}
            </p>
          </div>
        </div>
      </section>
      <section id="help" className="bg-mist">
        <div className={section}>
          <span className="text-[11px] font-semibold uppercase text-sage">
            02 / {t("landing.helpLabel")}
          </span>
          <h2 className="display whitespace-pre-line text-4xl leading-tight text-ink sm:text-5xl">
            {t("landing.helpTitle")}
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            {helps.map(([title, text]) => (
              <article key={title} className="rounded-2xl bg-white p-7">
                <div className="mb-6 flex h-11 w-11 items-center justify-center rounded-lg bg-[#E9F0E8]">
                  <Check className="text-sage" />
                </div>
                <h3 className="text-xl font-medium">{title}</h3>
                <p className="mt-4 leading-7 text-muted">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section id="steps" className={section}>
        <span className="text-[11px] font-semibold uppercase text-sage">
          03 / {t("landing.stepsLabel")}
        </span>
        <h2 className="display whitespace-pre-line text-4xl leading-tight text-ink sm:text-5xl">
          {t("landing.stepsTitle")}
        </h2>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <article key={step}>
              <div className="flex items-center gap-4">
                <span className="display text-4xl text-sage">0{i + 1}</span>
                <span className="h-px flex-1 bg-line" />
              </div>
              <h3 className="mt-5 text-xl font-medium">{step}</h3>
              <p className="mt-3 leading-7 text-muted">{stepTexts[i]}</p>
            </article>
          ))}
        </div>
      </section>
      <section id="chat" className={section}>
        <div className="grid gap-10 lg:grid-cols-[380px_1fr] lg:gap-24">
          <div>
            <span className="text-[11px] font-semibold uppercase text-sage">
              05 / {t("landing.chatLabel")}
            </span>
            <h2 className="display mt-6 whitespace-pre-line text-4xl leading-tight text-ink sm:text-5xl">
              {t("landing.chatTitle")}
            </h2>
            <p className="mt-6 leading-7 text-muted">{t("landing.chatText")}</p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-line bg-white">
            <div className="flex items-center gap-3 border-b border-line p-5">
              <MessageCircle className="text-sage" />
              <div>
                <div className="font-semibold">{t("landing.consultant")}</div>
                <div className="text-xs text-muted">{t("landing.service")}</div>
              </div>
            </div>
            <div ref={messagesEndRef} className="flex min-h-44 max-h-80 flex-col gap-3 overflow-y-auto bg-mist p-6 text-sm" aria-live="polite">
              {messages.length ? messages.map((message) => {
                const isVisitorMessage = message.sender_user_id === null;
                return (
                  <div
                    key={message.id}
                    className={`max-w-[85%] rounded-xl px-3 py-2 ${isVisitorMessage ? "self-end bg-navy text-white" : "self-start bg-white text-ink"}`}
                  >
                    {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}
                    {message.attachment && <p className="mt-1 break-all underline">{message.attachment.original_name}</p>}
                    <time dateTime={message.created_at} className={`mt-1 block text-[10px] ${isVisitorMessage ? "text-white/70" : "text-muted"}`}>
                      {new Date(message.created_at).toLocaleString()}
                    </time>
                  </div>
                );
              }) : <p className="text-muted">{sent ? t("landing.chatSent") : t("landing.chatEmpty")}</p>}
            </div>
            <form onSubmit={submitChat} className="space-y-2 p-4">
              <input
                type="email"
                value={chatEmail}
                onChange={(e) => setChatEmail(e.target.value)}
                placeholder={t("landing.chatEmail")}
                onBlur={confirmChatEmail}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void confirmChatEmail();
                  }
                }}
                className="h-10 w-full rounded-lg border border-line px-3 outline-none"
              />
              <p className="text-xs text-muted">{emailSaved ? t("landing.chatEmailSaved") : t("landing.chatEmailHint")}</p>
              <div className="flex gap-2">
                <input
                  required
                  value={chat}
                  onChange={(e) => setChat(e.target.value)}
                  disabled={sendingChat}
                  placeholder={t("landing.chatPlaceholder")}
                  className="min-w-0 flex-1 rounded-lg border border-line px-3 outline-none"
                />
                <button
                  type="submit"
                  disabled={sendingChat || !chat.trim()}
                  aria-label={t("landing.send")}
                  aria-busy={sendingChat}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-navy text-white disabled:cursor-wait disabled:opacity-60"
                >
                  {sendingChat ? <LoaderCircle size={17} className="animate-spin" aria-hidden="true" /> : <Send size={17} />}
                </button>
              </div>
              {chatError && <p className="text-xs text-red-700" role="alert">{t("app.actionError")}</p>}
            </form>
          </div>
        </div>
      </section>
      <section id="faq" className={section}>
        <div className="grid gap-10 lg:grid-cols-[380px_1fr] lg:gap-24">
          <div>
            <h2 className="display whitespace-pre-line text-4xl leading-tight text-ink sm:text-5xl">
              {t("landing.faqTitle")}
            </h2>
            <p className="mt-6 leading-7 text-muted">{t("landing.faqIntro")}</p>
          </div>
          <div>
            {faq.map(({ question, answer }) => (
              <details key={question} className="border-t border-line py-5">
                <summary className="cursor-pointer text-lg font-medium">
                  {question}
                </summary>
                <p className="mt-4 whitespace-pre-line leading-7 text-muted">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <section className="mx-4 mb-16 rounded-3xl bg-navy p-7 text-white sm:mx-10 sm:p-12 lg:mx-20">
        <h2 className="display whitespace-pre-line text-4xl leading-tight sm:text-5xl">
          {t("landing.ctaTitle")}
        </h2>
        <Link
          to="/login"
          className="mt-8 inline-flex items-center gap-3 rounded-lg bg-white px-6 py-4 font-semibold text-navy"
        >
          {t("nav.start")}
          <ArrowRight size={18} />
        </Link>
      </section>
      <footer className="mx-auto flex max-w-[1440px] flex-col gap-8 px-5 pb-10 sm:px-10 lg:px-20">
        <p className="text-sm text-muted">{t("landing.independent")}</p>
        <div className="h-px bg-line" />
        <p className="text-xs leading-6 text-muted">{t("landing.legal")}</p>
        <a
          href="https://dvprogram.state.gov"
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-navy"
        >
          dvprogram.state.gov
        </a>
      </footer>
    </main>
  );
}
